// JPEG (§9): the browser's encoder makes the entropy-coded data; we splice our own segments after SOI.
//  1. Pixels (already quantized, alpha flattened, in the OUTPUT space's encoding) go into an
//     OffscreenCanvas created with colorSpace 'srgb' through an 'srgb' ImageData, so the browser does
//     not convert them. convertToBlob({ type: 'image/jpeg', quality }).
//  2. Existing APP0/APP1/APP2 segments are dropped; we write APP0 JFIF (with DPI), APP1 EXIF,
//     APP1 XMP and APP2 ICC_PROFILE (split into 65,519-byte chunks with sequence numbers).
export const XMP_HEADER = 'http://ns.adobe.com/xap/1.0/\0';
const ICC_HEADER = 'ICC_PROFILE\0';
export const ICC_CHUNK = 65519;

export type JpegSegments = {
  exif?: Uint8Array | null;
  xmp?: string | null;
  icc?: Uint8Array | null;
  dpi?: number;
};

const ascii = (s: string) => Uint8Array.from(s, (c) => c.charCodeAt(0));

function segment(marker: number, body: Uint8Array): Uint8Array {
  if (body.length + 2 > 0xffff) throw new Error(`JPEG segment too large (${body.length} bytes)`);
  const out = new Uint8Array(4 + body.length);
  out[0] = 0xff;
  out[1] = marker;
  out[2] = (body.length + 2) >> 8;
  out[3] = (body.length + 2) & 255;
  out.set(body, 4);
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function jfifSegment(dpi: number): Uint8Array {
  const d = Math.max(1, Math.min(65535, Math.round(dpi)));
  return segment(
    0xe0,
    new Uint8Array([0x4a, 0x46, 0x49, 0x46, 0, 1, 1, 1, d >> 8, d & 255, d >> 8, d & 255, 0, 0]),
  );
}

export function iccSegments(icc: Uint8Array): Uint8Array[] {
  const n = Math.ceil(icc.length / ICC_CHUNK);
  const out: Uint8Array[] = [];
  for (let i = 0; i < n; i++) {
    const chunk = icc.subarray(i * ICC_CHUNK, (i + 1) * ICC_CHUNK);
    out.push(segment(0xe2, concat([ascii(ICC_HEADER), new Uint8Array([i + 1, n]), chunk])));
  }
  return out;
}

/** Splits a JPEG into its marker segments up to SOS; returns [segments, scan data from SOS onward]. */
export function parseJpeg(b: Uint8Array): {
  segments: { marker: number; data: Uint8Array }[];
  rest: Uint8Array;
} {
  if (b[0] !== 0xff || b[1] !== 0xd8) throw new Error('not a JPEG');
  const segments: { marker: number; data: Uint8Array }[] = [];
  let o = 2;
  while (o < b.length) {
    if (b[o] !== 0xff) throw new Error(`JPEG: expected marker at ${o}`);
    const marker = b[o + 1]!;
    if (marker === 0xda) return { segments, rest: b.subarray(o) };
    const len = (b[o + 2]! << 8) | b[o + 3]!;
    segments.push({ marker, data: b.subarray(o, o + 2 + len) });
    o += 2 + len;
  }
  throw new Error('JPEG: no SOS');
}

/** Replaces APP0/APP1/APP2 in an existing JPEG with ours. Pure (testable without a browser). */
export function spliceJpeg(jpeg: Uint8Array, seg: JpegSegments): Uint8Array {
  const { segments, rest } = parseJpeg(jpeg);
  const keep = segments
    .filter((s) => s.marker !== 0xe0 && s.marker !== 0xe1 && s.marker !== 0xe2)
    .map((s) => s.data);
  const ours: Uint8Array[] = [jfifSegment(seg.dpi ?? 72)];
  if (seg.exif) ours.push(segment(0xe1, seg.exif));
  if (seg.xmp) ours.push(segment(0xe1, concat([ascii(XMP_HEADER), new TextEncoder().encode(seg.xmp)])));
  if (seg.icc) ours.push(...iccSegments(seg.icc));
  return concat([new Uint8Array([0xff, 0xd8]), ...ours, ...keep, rest]);
}

/** Encodes opaque RGBA8 (output-space encoded) to JPEG with our segments. Browser/worker only. */
export async function encodeJpeg(
  rgba: Uint8Array,
  width: number,
  height: number,
  quality: number,
  seg: JpegSegments,
): Promise<Uint8Array> {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d', { colorSpace: 'srgb' });
  if (!ctx) throw new Error('2D canvas unavailable');
  const data = new Uint8ClampedArray(rgba.buffer as ArrayBuffer, rgba.byteOffset, rgba.byteLength);
  ctx.putImageData(new ImageData(data, width, height, { colorSpace: 'srgb' }), 0, 0);
  const blob = await canvas.convertToBlob({
    type: 'image/jpeg',
    quality: Math.min(1, Math.max(0.01, quality / 100)),
  });
  return spliceJpeg(new Uint8Array(await blob.arrayBuffer()), seg);
}
